import {
  compressToUTF16 as compress,
  decompressFromUTF16 as decompress
} from 'lz-string';

// Keep everything unless a module explicitly provides its own (de)serializer.
// For `detailText`, we make sure the SSR doc (payload.text, titleTag, metaDescription)
// is preserved verbatim so hydration doesn’t fall back to the default article.

const DEFAULT_STATE = {};

/** Ensure we keep the SSR-rendered detail doc intact */
function normalizeDetailTextSlice(slice) {
  if (!slice || typeof slice !== 'object') return slice;
  const { payload, filter, offerType } = slice;

  // Preserve payload (doc with `.text`, and optional `titleTag`, `metaDescription`)
  // Drop ephemeral flags to reduce payload size (they'll be reset by the reducer).
  return {
    payload: payload || null,
    filter: filter || null,
    offerType: offerType || null,
    loading: false,
    error: null
  };
}

function serializeStores(state) {
  return Object.keys(state).reduce((acc, name) => {
    const store = state[name];
    try {
      // If a module provides its own serializer, use it.
      const mod = require(`./modules/${name}`);
      if (typeof mod.serialize === 'function') {
        acc[name] = mod.serialize(store);
      } else {
        // No custom serializer: apply targeted normalization for critical slices.
        if (name === 'detailText') {
          acc[name] = normalizeDetailTextSlice(store);
        } else {
          acc[name] = store;
        }
      }
    } catch (err) {
      // Module not found / no custom serializer — keep as-is, with our detailText guard above.
      acc[name] = name === 'detailText' ? normalizeDetailTextSlice(store) : store;
    }
    return acc;
  }, {});
}

export function serialize(state = DEFAULT_STATE) {
  const str = JSON.stringify(serializeStores(state));

  // In dev we keep the plain JSON (easier to inspect in View Source / DevTools)
  if (__DEV__) {
    return str;
  }

  // In prod we compress to shrink HTML size
  return compress(str);
}

function deserializeStores(state) {
  return Object.keys(state).reduce((acc, name) => {
    const store = state[name];
    try {
      const mod = require(`./modules/${name}`);
      const { deserialize, initialState } = mod;

      if (typeof deserialize === 'function') {
        acc[name] = deserialize(store);
      } else if (initialState && store) {
        // Merge onto module's initialState to restore defaults
        acc[name] = { ...initialState, ...store };
      } else {
        acc[name] = store;
      }
    } catch (err) {
      acc[name] = store;
    }
    return acc;
  }, {});
}

export function deserialize(str) {
  if (!str) return DEFAULT_STATE;

  try {
    // In production on the client, the payload is compressed
    if (!__DEV__ && __CLIENT__) {
      str = decompress(str);
    }
    const parsed = JSON.parse(str);
    return deserializeStores(parsed);
  } catch (err) {
    return DEFAULT_STATE;
  }
}
